import { useCallback, useState } from 'react'
import TimeAgo from 'react-timeago'
import { HatGlasses } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Menu, Tooltip } from '@highlight/ui'
import { useIncognitoMode, type IncognitoSource } from '@renderer/hooks/useIncognitoMode'
import {
  useAudioNoteHasMeeting,
  useIsAudioNotePaused,
  useIsTakingNotes,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { INCOGNITO_EXPLAINER, durationUntilMorning } from '@shared/utils/incognito'
import { noteNoun } from '@shared/utils/audio-notes/AudioNoteUtils'
import { incognitoTimeRemainingFormatter } from '@renderer/utils/incognito-time-remaining'
import { ACTIVE_TOOLBAR_BUTTON_CLASS } from './toolbar-button-class'

const ONE_HOUR_MS = 60 * 60 * 1000

interface SidepanelIncognitoButtonProps {
  /** Render as a submenu of an open menu instead of a button with its own menu. */
  submenu?: boolean
  source?: IncognitoSource
  tooltipPosition?: 'top' | 'bottom'
  className?: string
  onOpenChange?: (open: boolean) => void
}

/**
 * Incognito toggle. As a top-bar button it turns green while incognito is on, so
 * the "capture is paused" state is legible without opening anything. As a
 * submenu that state shows only in its label.
 */
export function SidepanelIncognitoButton({
  submenu = false,
  source = 'sidepanel-toolbar',
  tooltipPosition = 'bottom',
  className,
  onOpenChange,
}: SidepanelIncognitoButtonProps) {
  const { isIncognitoEnabled, enableIncognito, disableIncognito, incognitoTimer } = useIncognitoMode()
  // Narrow selectors — this button is mounted in the toolbar for the whole
  // session, so the aggregate hook would re-render it on every recording tick.
  const isTakingNotes = useIsTakingNotes()
  const isPaused = useIsAudioNotePaused()
  const hasMeeting = useAudioNoteHasMeeting()
  const [isOpen, setIsOpen] = useState(false)

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      onOpenChange?.(open)
    },
    [onOpenChange],
  )

  // Incognito can't start mid-meeting, but it can always be turned off.
  const blockedByMeeting = (isTakingNotes || isPaused) && !isIncognitoEnabled

  const handleIncognitoOff = useCallback(async () => {
    try {
      await disableIncognito(source)
    } catch (error) {
      console.error('Failed to turn off incognito mode:', error)
    }
  }, [disableIncognito, source])

  const handleIncognitoOneHour = useCallback(async () => {
    try {
      await enableIncognito(ONE_HOUR_MS, source)
    } catch (error) {
      console.error('Failed to enable incognito mode:', error)
    }
  }, [enableIncognito, source])

  const handleIncognitoUntilMorning = useCallback(async () => {
    try {
      await enableIncognito(durationUntilMorning(), source)
    } catch (error) {
      console.error('Failed to enable incognito mode:', error)
    }
  }, [enableIncognito, source])

  const label = isIncognitoEnabled ? 'Incognito mode (on)' : 'Incognito mode'

  const content = (
    <Menu.Content align={submenu ? undefined : 'end'}>
      {/* Both states lead with what incognito does, since while it's running is
          when the explanation matters most (HIG-5832). */}
      <Menu.Group>
        <Menu.GroupLabel className="max-w-48 whitespace-normal">
          {blockedByMeeting ? `End your ${noteNoun(hasMeeting)} first.` : INCOGNITO_EXPLAINER}
        </Menu.GroupLabel>
      </Menu.Group>
      <Menu.Separator />

      {isIncognitoEnabled ? (
        <Menu.Item onClick={handleIncognitoOff}>
          <span>Turn off</span>
          {incognitoTimer && (
            <span className="text-foreground-secondary type-ui-xs ml-auto tabular-nums">
              <TimeAgo date={incognitoTimer} formatter={incognitoTimeRemainingFormatter} minPeriod={60} live={true} />
            </span>
          )}
        </Menu.Item>
      ) : (
        <>
          <Menu.Item onClick={handleIncognitoOneHour} disabled={blockedByMeeting}>
            <span>For 1 hour</span>
          </Menu.Item>
          <Menu.Item onClick={handleIncognitoUntilMorning} disabled={blockedByMeeting}>
            <span>Until morning</span>
          </Menu.Item>
        </>
      )}
    </Menu.Content>
  )

  if (submenu) {
    return (
      <Menu.SubmenuRoot>
        <Menu.SubmenuTrigger>
          <HatGlasses aria-hidden="true" />
          <Menu.ItemText>{label}</Menu.ItemText>
        </Menu.SubmenuTrigger>
        {content}
      </Menu.SubmenuRoot>
    )
  }

  return (
    <Menu open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip>
        <Tooltip.Trigger
          render={
            <Menu.Trigger
              variant="ghost"
              shape="square"
              aria-label={label}
              className={twMerge(
                // `tertiary` re-asserts a neutral text/bg on hover, so the active
                // state has to pin both or the green flips grey under the cursor.
                isIncognitoEnabled &&
                  'bg-success-subtle border-success text-success-subtle hover:bg-success-subtle-hover hover:text-success-subtle border',
                !isIncognitoEnabled && isOpen && ACTIVE_TOOLBAR_BUTTON_CLASS,
                className,
              )}
            />
          }
        >
          <HatGlasses aria-hidden="true" />
        </Tooltip.Trigger>
        <Tooltip.Content side={tooltipPosition}>{label}</Tooltip.Content>
      </Tooltip>

      {content}
    </Menu>
  )
}
